import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export type LeaveRequestPayload = {
  leaveType: string;
  reason?: string;
  fromDate: string;
  toDate: string;
  departmentId?: string;
  convertToUnpaid?: boolean;
};

export interface Leave {
  _id: string;
  leaveType: string;
  reason?: string;
  fromDate: string;
  toDate: string;
  departmentId?: string;
  convertToUnpaid?: boolean;
  status?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type LeaveRequestResponse = {
  success: boolean;
  message: string;
  data: Leave;
};

const updateLeaveRequest = async (
  id: string,
  payload: LeaveRequestPayload
): Promise<LeaveRequestResponse> => {
  const { data } = await customAxios.put<LeaveRequestResponse>(
    `/leaves/put-leave-request/${id}`,
    payload
  );
  return data;
};

export const useUpdateLeaveRequest = () => {
  const queryClient = useQueryClient();

  return useMutation<
    LeaveRequestResponse,
    AxiosError,
    { id: string; payload: LeaveRequestPayload }
  >(({ id, payload }) => updateLeaveRequest(id, payload), {
    onSuccess: async (data, variables) => {
      showNotification({
        title: "Success",
        message: data.message ?? "Leave request updated",
        color: "green",
      });

      await queryClient.invalidateQueries(["leaves"]);
      await queryClient.invalidateQueries(["leave", variables.id]);
    },
    onError: (error) =>
      showNotification({
        title: "Error",
        message:
          (error.response?.data as { message?: string })?.message ??
          "Failed to update leave request",
        color: "red",
      }),
  });
};
